import { html } from "lit-html";
import i18next from "i18next";
import classNames from "classnames";
import { useRef } from "Shared/haunted/CustomHooks";
import PerfectScrollbar from "perfect-scrollbar";
import { stationSelectorCountryArrowDesktop } from "./newFlightSvgs";
import { ALL_COUNTRY_CODE, stationUtils } from "./stationUtils";
import { useEffectWithoutCleanup, useMemo, useState } from "Shared/haunted/CustomHooks";
import { ref } from "Components/directives/ref";
import { City, Country } from "../RoutePickerModel";
import { INBOUND, OUTBOUND } from "Services/constants";
import { DynamicSettings } from "Components/admin/DynamicSettings";
import { stationCommonTemplates } from "./stationCommonTemplates";
import { isTabletOrMobileResolution } from "Services/common";
import { allStationsPlaceholder } from "ComponentHelpers/StationSelectorHelper";

export interface Props {
	countries: Country[];
	culture: string;
	destinationCity: City;
	dynamicSettings: DynamicSettings;
	isOpen: boolean;
	journeyIndex: number;
	originCity: City;
	onClick: (city: City) => void;
	onInput: () => void;
}

export const useStationSelectorDropdown = (props: Props) => {
	const scroller = useRef<HTMLDivElement>(null);

	const [selectedCountryCode, setSelectedCountryCode] = useState<string>("");
	const [filterExpression, setFilterExpression] = useState<string>("");

	const { getFilteredModel, getDefaultCountryCodeFromSettings } = stationUtils();
	const { noResultTemplate } = stationCommonTemplates();

	const filteredModel = useMemo(
		() => getFilteredModel(props.countries, filterExpression) || [],
		[props.countries, filterExpression]
	);

	const orderedCountries = useMemo(() => {
		if (!filteredModel?.length) return [];

		const selectedCountry = filteredModel.find((country) => country.code === selectedCountryCode);

		return selectedCountry && selectedCountryCode !== ALL_COUNTRY_CODE
			? [
					selectedCountry,
					allStationsPlaceholder,
					...filteredModel.filter((country) => country.code !== selectedCountryCode),
			  ]
			: [allStationsPlaceholder, ...filteredModel];
	}, [selectedCountryCode, filteredModel]);

	const init = () => {
		if (!props.dynamicSettings || !orderedCountries.length || selectedCountryCode) return;

		const defaultCountry = getDefaultCountryCodeFromSettings(
			props.journeyIndex,
			props.culture,
			props.dynamicSettings
		);

		const cultureBasedCountry = orderedCountries.find(
			(country) => country.code.toLowerCase() === defaultCountry.toLowerCase()
		);

		setSelectedCountryCode(cultureBasedCountry?.code || ALL_COUNTRY_CODE);
	};

	const initScrollbar = () => {
		let scrollbar: PerfectScrollbar;

		window.setTimeout(() => {
			if (scroller.current?.parentElement) {
				scrollbar = new PerfectScrollbar(scroller.current.parentElement, {
					wheelPropagation: false,
					wheelSpeed: 2,
					swipeEasing: true,
					suppressScrollX: true,
				});
			}
		}, 100);

		return () => scrollbar?.destroy();
	};

	// DEVNOTE We put the longest list with the shortest list and so on to make as many fit as possible
	const groupedCountries = useMemo(() => {
		if (!orderedCountries?.length) return [];

		const sorted = orderedCountries
			.filter((country) => country.code !== ALL_COUNTRY_CODE)
			.sort((a, b) => a.cities.length - b.cities.length);

		return Array.from({ length: Math.ceil(sorted.length / 2) }, (_, i) => [
			sorted.length - 1 - i > i ? sorted[sorted.length - 1 - i] : null,
			sorted[i],
		] as Country[]);
	}, [orderedCountries]);

	const isReady = props.countries && selectedCountryCode && (filterExpression || groupedCountries?.length);

	const handleCountryClick = (e: MouseEvent, country: Country) => {
		e.preventDefault();
		e.stopPropagation();
		setSelectedCountryCode(country.code);
	};

	const handleInput = (e: KeyboardEvent) => {
		props.onInput();
		setFilterExpression((e.target as HTMLInputElement).value);
	};

	const handleCityClick = (city: City) => {
		setFilterExpression("");
		setSelectedCountryCode(city.countryCode);
		props.onClick(city);
	};

	useEffectWithoutCleanup(() => {
		if (!filteredModel?.some((c) => c.code === selectedCountryCode) && selectedCountryCode !== ALL_COUNTRY_CODE) {
			const firstCountry = filteredModel?.[0]?.code;
			if (firstCountry) setSelectedCountryCode(firstCountry);
		}
	}, [props.isOpen, selectedCountryCode, filteredModel]);

	useEffectWithoutCleanup(() => {
		if (filterExpression) setSelectedCountryCode(ALL_COUNTRY_CODE);
	}, [filterExpression]);

	useEffectWithoutCleanup(initScrollbar, [props.countries, selectedCountryCode]);

	useEffectWithoutCleanup(init, [props.isOpen, orderedCountries, props.dynamicSettings, selectedCountryCode]);

	useEffectWithoutCleanup(() => {
		const newCountryCode =
			props.journeyIndex === OUTBOUND ? props.originCity?.countryCode : props.destinationCity?.countryCode;
		if (newCountryCode && newCountryCode !== selectedCountryCode) setSelectedCountryCode(newCountryCode);
	}, [props.originCity?.code, props.destinationCity?.code]);

	const countryNameTemplate = (countryName: string, index: number) => html`
		<span
			class=${classNames("whitespace-nowrap font-body text-lg font-normal leading-5 text-n-blue", {
				"group-hover:text-n-cyan": index !== 0,
				"text-[#636363]": index !== 0,
				"text-white": index === 0,
			})}
		>
			${countryName}
		</span>
	`;

	const countryArrowTemplate = (index: number) => html`
		<span
			class=${classNames("mt-1 block", {
				"text-[#636363]": index !== 0,
				"text-white": index === 0,
				"group-hover:text-n-cyan": index !== 0,
			})}
		>
			${stationSelectorCountryArrowDesktop}
		</span>
	`;

	const countryTemplate = (country: Country, index: number) => html`
		<li
			class=${classNames("group flex h-10 cursor-pointer items-center justify-between gap-x-4 px-5", {
				"bg-n-cyan": index === 0,
			})}
			@click=${(e: MouseEvent) => handleCountryClick(e, country)}
		>
			${countryNameTemplate(country.name, index)} ${countryArrowTemplate(index)}
		</li>
	`;

	const cityTemplate = (city: City) => html`
		<li
			class="group flex w-full cursor-pointer justify-between gap-x-4 font-body text-sm leading-[18px]"
			@click=${() => handleCityClick(city)}
		>
			<span class="text-[#444444] group-hover:text-n-cyan"> ${city.name} </span>
			<span class="text-n-light-gray group-hover:text-n-cyan"> ${city.code} </span>
		</li>
	`;

	const citiesTemplate = () => html`
		<li class="mb-2 block font-body text-base font-bold leading-5 text-n-cyan">${i18next.t("Ciudades")}</li>
		${filteredModel.find((country) => country.code === selectedCountryCode)?.cities.map(cityTemplate)}
	`;

	const allCountriesCountryTemplate = (country: Country, index: number) =>
		country
			? html`
					<div
						class=${classNames("mb-2 border-b border-solid border-[#e0e0e0] pb-2", {
							"mb-0 border-none pb-0": index !== 0,
						})}
					>
						<div class="mb-2 font-body text-base font-bold leading-5 text-n-cyan">${country.name}</div>
						<ul>
							${country.cities.map(cityTemplate)}
						</ul>
					</div>
			  `
			: "";

	const countryGroupTemplate = (countryGroup: Country[]) => html`
		<div>${countryGroup.map(allCountriesCountryTemplate)}</div>
	`;

	const allDestinationsTemplate = () => html`
		<li ref=${ref(scroller)} class="relative grid h-[416px] grid-cols-[auto_auto_auto_auto] gap-x-10">
			${groupedCountries.map(countryGroupTemplate)}
		</li>
	`;

	const contentTemplate = () => html`
		<div class="flex max-h-full items-stretch overflow-hidden rounded-lg bg-white">
			<ul class="block flex-1">
				${orderedCountries.map(countryTemplate)}
			</ul>
			<ul class="max-h-[416px] min-w-52 shrink-0 overflow-hidden px-5 py-5">
				${selectedCountryCode === ALL_COUNTRY_CODE ? allDestinationsTemplate() : citiesTemplate()}
			</ul>
		</div>
	`;

	const dropdownContentTemplate = () => (orderedCountries?.length ? contentTemplate() : noResultTemplate());

	const inputValue = (props.journeyIndex === OUTBOUND ? props.originCity?.name : props.destinationCity?.name) || "";

	const inputTemplate = () => html`
		<input
			?readonly=${isTabletOrMobileResolution()}
			class=${classNames(
				"w-full cursor-pointer appearance-none border-none bg-transparent px-0 pb-0 pt-0.5 text-base outline-hidden",
				"placeholder:text-base/none placeholder:text-[#c1c1c1]",
				"focus:cursor-text",
				{
					"text-n-blue": inputValue,
				}
			)}
			placeholder=${props.journeyIndex === OUTBOUND ? i18next.t("Origen") : i18next.t("Destino")}
			value=${filterExpression || inputValue || ""}
			.value=${filterExpression || inputValue || ""}
			@input=${handleInput}
		/>
	`;

	const dropdownTemplate = () => html`
		<ac-dropdown2 .content=${dropdownContentTemplate()} .isOpen=${props.isOpen}></ac-dropdown2>
	`;

	const htmlTemplate = () =>
		isReady || props.journeyIndex === INBOUND ? html` ${inputTemplate()} ${dropdownTemplate()} ` : "";

	return { htmlTemplate };
};
